import '@styles/global.css';
import '@styles/normalize.css';
import '@staging-design-system/tokens/index.css';
import api from '@api';
import Sidebar from '@components/layout/sidebar/Sidebar';
import config from '@config';
import createCache from '@emotion/cache';
import { CacheProvider, css, Global } from '@emotion/react';
import { Log } from '@enums';
import { ThemeProvider } from '@mui/material';
import Intercom from '@platform2-lib/intercom';
import type { User } from '@platform2-lib/models/models';
import { useChatStore } from '@store/chat';
import { useProfileStore } from '@store/profile';
import { useShellStore } from '@store/shell';
import { theme } from '@theme';
import { isSettingsPath, log } from '@utils';
import { useCallback, useEffect } from 'react';
import Styled, { getColorScheme } from './App.styled';
import ErrorBoundary from './ErrorBoundary';
import MainContent from './main/MainContent';

const cache = createCache({ key: 'octopus' });

if (config.env !== 'production') {
  log('Octopus', { background: theme.palette.primary.main });
  console.info(JSON.stringify(config, null, 2));
}

const release = window.localStorage.getItem('octopus.release');

if (release !== config.release) {
  window.localStorage.setItem('octopus.release', config.release);
  window.localStorage.removeItem('octopus.agent');
}

interface Props {
  user: User;
}

function App({ user }: Props) {
  const { setUser, feat } = useProfileStore();
  const shellStore = useShellStore();
  const clearChat = useChatStore((state) => state.clear);
  const browserPathname = useShellStore((state) => state.browserPathname);

  useEffect(() => {
    const base = browserPathname.split('/chat').at(0) ?? '';
    const normalizedBase = base === '/' ? '' : base;
    if (isSettingsPath(browserPathname)) {
      const sectionBase = browserPathname.replace(/\/settings\/?$/, '');
      const normalizedSectionBase = sectionBase === '/' ? '' : sectionBase;
      if (shellStore.pathname !== normalizedSectionBase) {
        shellStore.setPathname(normalizedSectionBase);
      }
      return;
    }
    if (shellStore.pathname !== normalizedBase) {
      shellStore.setPathname(normalizedBase);
    }
  }, [browserPathname, shellStore.pathname, shellStore.setPathname]);

  const handleOffline = useCallback(() => {
    log('The network connection has been lost.', Log.Warning);
    shellStore.setIsOffline(true);
    shellStore.setToast({
      message: 'No internet connection',
      status: 'error',
      timeout: 3600000 // 1hr
    });
  }, [shellStore.setIsOffline, shellStore.setToast]);

  const handleOnline = useCallback(() => {
    log('The network connection has been restored.', { color: 'green' });
    shellStore.setIsOffline(false);
    shellStore.setToast({ message: '', isOpen: false });
  }, [shellStore.setIsOffline, shellStore.setToast]);

  const handlePopState = useCallback(() => {
    const path = window.location.pathname;
    const { setBrowserPathname, pathname, reset } = useShellStore.getState();
    setBrowserPathname(path);
    if (pathname === path) {
      reset();
      clearChat();
      log('Reset store values.', Log.Notice);
    }
  }, [clearChat]);

  useEffect(() => {
    setUser(user);
  }, [user, setUser]);

  useEffect(() => {
    const appId = config.intercom.appId;
    if (!appId || !user.email) {
      return;
    }

    Intercom.boot({
      app_id: appId,
      email: user.email,
      user_hash: user.intercom_user_hash,
      platformVersion: 'Octopus'
    });
  }, [user]);

  useEffect(() => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [handleOffline, handleOnline]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  useEffect(() => {
    api.analytics.openedApp();
  }, []);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <Global styles={[getColorScheme(feat.darkMode.enabled), rootStyle]} />
        <Styled.Layout hasDarkMode={feat.darkMode.enabled}>
          <ErrorBoundary>
            <Sidebar />
            <MainContent />
          </ErrorBoundary>
        </Styled.Layout>
      </ThemeProvider>
    </CacheProvider>
  );
}

const rootStyle = css`
  #octopus-root {
    @media (max-width: 768px) {
      flex-flow: column;
    }
  }
`;

export default App;
